import { useRef, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CaretIcon } from 'src/components/Icons/icons';
import { AppModal } from 'src/components/Modals/AppModal';
import {
    EVENT_ICON_OPTIONS,
    EventType,
    getEventIcon,
    getEventTypeColor,
} from 'src/domain/event';
import { EventIconGraphic, NoEventIconGraphic } from './EventIconGraphic';
import { EventFormValues, NO_EVENT_ICON } from './eventFormModel';

const EVENT_TYPE_OPTIONS = [
    EventType.internal,
    EventType.residents,
    EventType.group,
    EventType.relatives,
] as const;

export const EventTypePicker = ({
    control,
    disabled,
    openTypeDisabledReason,
    onTypeChange,
}: {
    control: Control<EventFormValues>;
    disabled?: boolean;
    openTypeDisabledReason?: string;
    onTypeChange: (type: EventType) => void;
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const closeAndRestoreFocus = () => {
        setIsOpen(false);
        window.requestAnimationFrame(() => triggerRef.current?.focus());
    };

    return (
        <Controller
            control={control}
            name="type"
            render={({ field }) => {
                const selectedColor = getEventTypeColor(field.value);
                const selectedTypeName = t(
                    `modules.calendar.form.types.${field.value}.label`,
                );

                return (
                    <fieldset className="w-full max-w-[280px]">
                        <legend className="mb-2 text-sm font-medium">
                            {t('modules.calendar.form.typeLabel')}
                        </legend>
                        <div className="relative">
                            <button
                                ref={triggerRef}
                                type="button"
                                className="text-input flex min-h-[42px] items-center gap-3 text-left disabled:cursor-default"
                                disabled={disabled}
                                aria-expanded={disabled ? undefined : isOpen}
                                aria-controls={
                                    isOpen ? 'event-type-options' : undefined
                                }
                                onClick={() => setIsOpen(current => !current)}
                                onBlur={field.onBlur}
                            >
                                <span
                                    className="h-5 w-5 flex-shrink-0 rounded-full border-2 border-white shadow-sm"
                                    style={{
                                        backgroundColor:
                                            selectedColor.background,
                                    }}
                                    aria-hidden="true"
                                />
                                <span className="min-w-0 flex-1 truncate">
                                    {selectedTypeName}
                                </span>
                                {!disabled ? (
                                    <span
                                        className={`relative h-3 w-3 flex-shrink-0 transition-transform ${
                                            isOpen ? 'rotate-180' : 'rotate-0'
                                        }`}
                                        aria-hidden="true"
                                    >
                                        <CaretIcon />
                                    </span>
                                ) : null}
                            </button>

                            {isOpen ? (
                                <AppModal
                                    show
                                    clickOutside={() => setIsOpen(false)}
                                >
                                    <div
                                        id="event-type-options"
                                        role="listbox"
                                        aria-label={t(
                                            'modules.calendar.form.typeLabel',
                                        )}
                                        className="absolute left-0 top-full z-30 mt-2 w-[320px] max-w-[calc(100vw-3rem)] overflow-hidden rounded-xl border border-woodyBrown border-opacity-15 bg-white p-1.5 shadow-lg"
                                        onKeyDown={event => {
                                            if (event.key === 'Escape') {
                                                event.preventDefault();
                                                closeAndRestoreFocus();
                                            }
                                        }}
                                    >
                                        {EVENT_TYPE_OPTIONS.map(option => {
                                            const color =
                                                getEventTypeColor(option);
                                            const typeName = t(
                                                `modules.calendar.form.types.${option}.label`,
                                            );
                                            const selected =
                                                field.value === option;
                                            const unavailable =
                                                option ===
                                                    EventType.residents &&
                                                Boolean(openTypeDisabledReason);

                                            return (
                                                <button
                                                    key={option}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={selected}
                                                    disabled={unavailable}
                                                    title={
                                                        unavailable
                                                            ? openTypeDisabledReason
                                                            : undefined
                                                    }
                                                    className={`flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-woodyBrown disabled:cursor-not-allowed disabled:opacity-50 ${
                                                        selected
                                                            ? 'bg-lightPeach'
                                                            : 'hover:bg-lightPeach hover:bg-opacity-50'
                                                    }`}
                                                    onClick={() => {
                                                        field.onChange(option);
                                                        onTypeChange(option);
                                                        closeAndRestoreFocus();
                                                    }}
                                                >
                                                    <span
                                                        className="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full border-2 border-white shadow-sm"
                                                        style={{
                                                            backgroundColor:
                                                                color.background,
                                                        }}
                                                        aria-hidden="true"
                                                    />
                                                    <span className="min-w-0">
                                                        <span className="block font-medium">
                                                            {typeName}
                                                        </span>
                                                        <span className="mt-0.5 block text-xs leading-4 text-woodyBrown/65">
                                                            {t(
                                                                `modules.calendar.form.types.${option}.description`,
                                                            )}
                                                        </span>
                                                        {unavailable && (
                                                            <span className="mt-1 block text-xs">
                                                                {
                                                                    openTypeDisabledReason
                                                                }
                                                            </span>
                                                        )}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </AppModal>
                            ) : null}
                        </div>
                    </fieldset>
                );
            }}
        />
    );
};

export const EventIconPicker = ({
    control,
    disabled,
}: {
    control: Control<EventFormValues>;
    disabled?: boolean;
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const closeAndRestoreFocus = () => {
        setIsOpen(false);
        window.requestAnimationFrame(() => triggerRef.current?.focus());
    };

    return (
        <Controller
            control={control}
            name="icon"
            render={({ field }) => {
                const selectedIcon = getEventIcon(field.value || undefined);

                return (
                    <fieldset>
                        <legend className="mb-2 text-sm font-medium">
                            {t('modules.calendar.form.iconLabel')}
                        </legend>
                        <div className="relative">
                            <button
                                ref={triggerRef}
                                type="button"
                                className="text-input flex h-[42px] w-[72px] items-center justify-between gap-2 px-3 disabled:cursor-default"
                                disabled={disabled}
                                aria-expanded={disabled ? undefined : isOpen}
                                aria-controls={
                                    isOpen ? 'event-icon-options' : undefined
                                }
                                onClick={() => setIsOpen(current => !current)}
                                onBlur={field.onBlur}
                            >
                                <span
                                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-xl leading-none"
                                    aria-hidden="true"
                                >
                                    {selectedIcon ? (
                                        <EventIconGraphic
                                            icon={selectedIcon.key}
                                            className="h-6 w-6"
                                        />
                                    ) : (
                                        <NoEventIconGraphic className="h-6 w-6 opacity-70" />
                                    )}
                                </span>
                                {!disabled ? (
                                    <span
                                        className={`relative h-3 w-3 flex-shrink-0 transition-transform ${
                                            isOpen ? 'rotate-180' : 'rotate-0'
                                        }`}
                                        aria-hidden="true"
                                    >
                                        <CaretIcon />
                                    </span>
                                ) : null}
                            </button>

                            {isOpen ? (
                                <AppModal
                                    show
                                    clickOutside={() => setIsOpen(false)}
                                >
                                    <div
                                        id="event-icon-options"
                                        role="group"
                                        aria-label={t(
                                            'modules.calendar.form.iconLabel',
                                        )}
                                        className="absolute left-0 top-full z-30 mt-2 grid w-[320px] max-w-[calc(100vw-3rem)] grid-cols-5 gap-2 rounded-xl border border-woodyBrown border-opacity-15 bg-white p-3 shadow-lg sm:grid-cols-6"
                                        onKeyDown={event => {
                                            if (event.key === 'Escape') {
                                                event.preventDefault();
                                                closeAndRestoreFocus();
                                            }
                                        }}
                                    >
                                        <button
                                            type="button"
                                            aria-pressed={
                                                field.value === NO_EVENT_ICON
                                            }
                                            aria-label={t(
                                                'modules.calendar.form.noIcon',
                                            )}
                                            className={`flex h-11 w-11 items-center justify-center rounded-xl text-xl leading-none transition-colors focus:outline-none focus:ring-2 focus:ring-woodyBrown focus:ring-offset-2 ${
                                                field.value === NO_EVENT_ICON
                                                    ? 'bg-lightPeach ring-2 ring-woodyBrown ring-offset-2'
                                                    : 'bg-black bg-opacity-[0.03] hover:bg-lightPeach'
                                            }`}
                                            onClick={() => {
                                                field.onChange(NO_EVENT_ICON);
                                                closeAndRestoreFocus();
                                            }}
                                        >
                                            <NoEventIconGraphic className="h-6 w-6 opacity-70" />
                                        </button>
                                        {EVENT_ICON_OPTIONS.map(option => {
                                            const iconName = t(
                                                `modules.calendar.form.icons.${option.key}`,
                                            );
                                            const selected =
                                                field.value === option.key;

                                            return (
                                                <button
                                                    key={option.key}
                                                    type="button"
                                                    aria-pressed={selected}
                                                    aria-label={iconName}
                                                    className={`flex h-11 w-11 items-center justify-center rounded-xl text-2xl leading-none transition-colors focus:outline-none focus:ring-2 focus:ring-woodyBrown focus:ring-offset-2 ${
                                                        selected
                                                            ? 'bg-lightPeach ring-2 ring-woodyBrown ring-offset-2'
                                                            : 'bg-black bg-opacity-[0.03] hover:bg-lightPeach'
                                                    }`}
                                                    onClick={() => {
                                                        field.onChange(
                                                            option.key,
                                                        );
                                                        closeAndRestoreFocus();
                                                    }}
                                                >
                                                    <EventIconGraphic
                                                        icon={option.key}
                                                        className="h-7 w-7"
                                                    />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </AppModal>
                            ) : null}
                        </div>
                    </fieldset>
                );
            }}
        />
    );
};
